import { Accent } from "@/theme";
import { Box, chakra, Table, Td, Text, Th, Tr } from "@chakra-ui/react";
import { MDXProvider, MDXProviderComponents } from "@mdx-js/react";
import Highlight, { defaultProps } from "prism-react-renderer";
import theme from "prism-react-renderer/themes/vsDark";
import React, { ReactElement, ReactNode } from "react";
import { isObject, isString } from "util";
import Heading from "./Heading";
import Link from "./Link";
import { NavbarMenuItem } from "./Navbar";
import SpacePage from "./SpacePage";
import SpaceSection from "./SpaceSection";

interface MdxPageProps {
  pageContext: {
    frontmatter: unknown;
  };
  children: ReactNode;
}

function ensureString(arg: any): string | undefined {
  return typeof arg === "string" ? arg : undefined;
}

const scala = {
  "triple-quoted-string": {
    pattern: /"""[\s\S]*?"""/,
    greedy: true,
    alias: "string",
  },
  string: {
    pattern: /("|')(?:\\.|(?!\1)[^\\\r\n])*\1/,
    greedy: true,
  },
  keyword: /<-|=>|\b(?:abstract|case|catch|class|def|do|else|extends|final|finally|for|forSome|if|implicit|import|lazy|match|new|null|object|override|package|private|protected|return|sealed|self|super|this|throw|trait|try|type|val|var|while|with|yield)\b/,
  number: /\b0x(?:[\da-f]*\.)?[\da-f]+|(?:\b\d+(?:\.\d*)?|\B\.\d+)(?:e\d+)?[dfl]?/i,
  builtin: /\b(?:String|Int|Long|Short|Byte|Boolean|Double|Float|Char|Any|AnyRef|AnyVal|Unit|Nothing)\b/,
  symbol: /'[^\d\s\\]\w*/,
};

const customProps = {
  ...defaultProps,
  Prism: {
    ...defaultProps.Prism,
    languages: { ...defaultProps.Prism.languages, scala },
  },
};

const mdxComponents: MDXProviderComponents = {
  p: ({ children }) => <Text my="4">{children}</Text>,
  h1: ({ children }) => (
    <Heading level="page" size="xl">
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading level="section" size="lg">
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading level="subsection" size="md">
      {children}
    </Heading>
  ),
  ul: ({ children }) => (
    <chakra.ul my="4" ml="8">
      {children}
    </chakra.ul>
  ),
  ol: ({ children }) => (
    <chakra.ol my="4" ml="8">
      {children}
    </chakra.ol>
  ),
  table: ({ children }) => <Table variant="simple">{children}</Table>,
  tr: ({ children }) => <Tr>{children}</Tr>,
  td: ({ children }) => <Td>{children}</Td>,
  th: ({ children }) => <Th>{children}</Th>,
  code: ({ children }) => (
    <Box my="4" p="8" bg="gray.800" borderRadius="xl">
      <Highlight
        {...customProps}
        code={children}
        theme={theme}
        language={"scala" as any}
      >
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <pre className={className} style={style}>
            {tokens.map((line, i) => {
              const lineProps = getLineProps({ line, key: i });
              return (
                <chakra.div px="5" {...lineProps}>
                  <chakra.span opacity={0.3} mr="6" fontSize="xs">
                    {i + 1}
                  </chakra.span>
                  {line.map((token, key) => (
                    <span {...getTokenProps({ token, key })} />
                  ))}
                </chakra.div>
              );
            })}
          </pre>
        )}
      </Highlight>
    </Box>
  ),
  a: ({ href, children }) => <Link to={href}>{children}</Link>,
};

export default function MdxPage(props: MdxPageProps): ReactElement {
  const { pageContext, children } = props;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const frontmatter = pageContext.frontmatter as any;

  const title = frontmatter.title as string;
  const accent = (frontmatter.accent as Accent | undefined) ?? "purple";
  const activeMenuItem = frontmatter.activeMenuItem as
    | NavbarMenuItem
    | undefined;

  return (
    <SpacePage title={title} accent={accent} activeMenuItem={activeMenuItem}>
      <SpaceSection bg="beige">
        {title && <Heading level="page">{title}</Heading>}
      </SpaceSection>
      <SpaceSection bg="white" mt="8" pt="16" pb="32" ct="sm">
        <MDXProvider components={mdxComponents}>{children}</MDXProvider>
      </SpaceSection>
    </SpacePage>
  );
}
